// hooks/useContratosAtivos.js
import { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

const CONTRATOS_STORAGE_KEY = 'contratosAtivos';
const CONTRATOS_CONTEXT_STORAGE_KEY = 'contratosAtivosContext';
const CONTRATOS_SESSION_STORAGE_KEY = 'contratosAtivosSessionKeyV2';
let contratosAtivosRequest = null;

const getAuthToken = () =>
  localStorage.getItem('jwtToken') ||
  localStorage.getItem('authToken') ||
  sessionStorage.getItem('token') ||
  '';

const getCleanToken = (token) => (token?.startsWith('Bearer ') ? token.slice(7) : token);

const getContratosSessionKey = (token) => {
  try {
    const cleanToken = getCleanToken(token);
    if (!cleanToken) return '';

    const decoded = jwtDecode(cleanToken);
    const idCliente =
      decoded?.id_cliente ??
      decoded?.idCliente ??
      decoded?.idClienteAipim ??
      decoded?.cliente_id ??
      '';
    const email = decoded?.email || decoded?.sub || '';
    const issuedAt = decoded?.iat || '';
    const expiresAt = decoded?.exp || '';

    return [idCliente, email, issuedAt, expiresAt].join(':');
  } catch (error) {
    return '';
  }
};

const hasCurrentSessionCache = (token) => {
  try {
    const sessionKey = getContratosSessionKey(token);
    return Boolean(sessionKey) && localStorage.getItem(CONTRATOS_SESSION_STORAGE_KEY) === sessionKey;
  } catch (error) {
    return false;
  }
};

const getStoredContratosAtivos = () => {
  try {
    const token = getAuthToken();
    if (!hasCurrentSessionCache(token)) return [];
    return JSON.parse(localStorage.getItem(CONTRATOS_STORAGE_KEY) || '[]');
  } catch (error) {
    return [];
  }
};

const getStoredContratosContext = () => {
  try {
    const token = getAuthToken();
    if (!hasCurrentSessionCache(token)) return {};
    return JSON.parse(localStorage.getItem(CONTRATOS_CONTEXT_STORAGE_KEY) || '{}');
  } catch (error) {
    return {};
  }
};

const getClientNameFromContracts = (contratos = []) => {
  if (!Array.isArray(contratos) || contratos.length === 0) return '';
  return contratos[0]?.nomeCliente || contratos[0]?.nome_cliente || '';
};

export default function useContratosAtivos() {
  const [contratosAtivos, setContratosAtivos] = useState(() => getStoredContratosAtivos());
  const [clientName, setClientName] = useState(() =>
    getStoredContratosContext().nomeCliente || getClientNameFromContracts(getStoredContratosAtivos())
  );
  const [clienteId, setClienteId] = useState(() => getStoredContratosContext().idCliente || null);
  const [usuario, setUsuario] = useState(() => getStoredContratosContext().usuario || null);
  const [loading, setLoading] = useState(() => {
    const token = getAuthToken();
    return Boolean(token) && !hasCurrentSessionCache(token);
  });

  const token = getAuthToken();
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchContratos = async () => {
      if (!token) return;

      if (hasCurrentSessionCache(token)) {
        const storedContratos = getStoredContratosAtivos();
        const storedContext = getStoredContratosContext();
        setContratosAtivos(storedContratos);
        setClientName(storedContext.nomeCliente || getClientNameFromContracts(storedContratos));
        setClienteId(storedContext.idCliente || null);
        setUsuario(storedContext.usuario || null);
        return;
      }

      setLoading(true);

      try {
        const cleanToken = getCleanToken(token);
        if (!contratosAtivosRequest) {
          contratosAtivosRequest = axios
            .get(`${baseURL}/cliente/contratos-ativos`, {
              headers: { Authorization: `Bearer ${cleanToken}` },
            })
            .then((response) => response.data || {})
            .finally(() => {
              contratosAtivosRequest = null;
            });
        }

        const responseData = await contratosAtivosRequest;
        const contratos = Array.isArray(responseData?.contratos) ? responseData.contratos : [];
        const context = {
          idCliente: responseData?.Id_Cliente ?? null,
          nomeCliente: responseData?.nomeCliente || '',
          usuario: responseData?.usuario || null,
        };
        setContratosAtivos(contratos);
        localStorage.setItem(CONTRATOS_STORAGE_KEY, JSON.stringify(contratos));
        localStorage.setItem(CONTRATOS_CONTEXT_STORAGE_KEY, JSON.stringify(context));
        localStorage.setItem(CONTRATOS_SESSION_STORAGE_KEY, getContratosSessionKey(token));
        setClientName(context.nomeCliente || getClientNameFromContracts(contratos));
        setClienteId(context.idCliente);
        setUsuario(context.usuario);

      } catch (error) {
        console.error('Erro ao buscar contratos ativos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContratos();
  }, [token, baseURL]);

  return { contratosAtivos, clientName, clienteId, usuario, loading };
}
